import { Sprout } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Badge } from '@/components/admin/shared/badge'
import {
  resolveSeedHighlight,
  resolveTopicSeedHighlight,
  type SeedHighlight,
} from '@/lib/recommendation/seed-tier-template'
import { averageSubtopicMastery } from '@/lib/recommendation/topic-mastery-rollup'
import { cn } from '@/lib/utils'

import type { SubtopicSeedOverride } from './_lib/apply-knowledge-map-seed'
import {
  SEED_BUCKET_ORDER,
  SEED_BUCKETS,
  type SeedBucket,
} from './_lib/seed-buckets'
import {
  buildDefaultSubtopicFields,
  diffOverrideFromDefaults,
  type SubtopicFieldForm,
} from './_lib/seed-form-state'
import {
  parseSeedDifficulty,
  parseSeedMastery,
  parseSeedStabilityDays,
} from './_lib/seed-override-parse'
import { DiffMarker } from './diff-marker'
import type {
  PersistedSeedSubtopicState,
  RecommendationEngineCatalogTopic,
  SeedKnowledgeMapPayload,
} from './recommendation-engine-context'
import { useRecommendationEngineMock } from './recommendation-engine-context'

/**
 * Knowledge-Map Seed tool — admin sub-affordance on the Setup tab
 * (#402, Phase 2). Sets initial mastery/stability/difficulty for every
 * subtopic from a starting bucket; optional per-subtopic overrides; emits one
 * `coach_seed` calc-log entry for the audit trail.
 */
export function SeedKnowledgeMapPanel() {
  const {
    topics,
    seedKnowledgeMap,
    engineEnabled,
    catalogTopics,
    catalogTopicCount,
    catalogSubtopicCount,
    lastAppliedSeedBucket,
    lastAppliedSeedAt,
    lastAppliedSeedCoachName,
    hasPendingSeedProcessing,
    persistedSeedSubtopicStates,
  } = useRecommendationEngineMock()
  const resolvedCatalogTopics = useMemo<RecommendationEngineCatalogTopic[]>(
    () =>
      catalogTopics ??
      topics.map((topic) => ({
        id: topic.id,
        slug: topic.id,
        name: topic.name,
        displayOrder: 0,
        subtopics: topic.subtopics.map((subtopic) => ({
          id: subtopic.id,
          slug: subtopic.id,
          name: subtopic.name,
          displayOrder: 0,
        })),
      })),
    [catalogTopics, topics],
  )
  const topicById = useMemo(
    () => new Map(topics.map((topic) => [topic.id, topic])),
    [topics],
  )
  const subtopicById = useMemo(
    () =>
      new Map(
        topics.flatMap((topic) =>
          topic.subtopics.map((subtopic) => [subtopic.id, subtopic] as const),
        ),
      ),
    [topics],
  )
  const [bucket, setBucket] = useState<SeedBucket>(
    lastAppliedSeedBucket ?? 'intermediate',
  )
  const [confirming, setConfirming] = useState(false)
  const [subtopicFields, setSubtopicFields] = useState<
    Record<string, SubtopicFieldForm>
  >({})
  /** Explicit include flag; omitted = current mock `isEnabled`. */
  const [subtopicIncluded, setSubtopicIncluded] = useState<
    Record<string, boolean>
  >({})

  useEffect(() => {
    if (lastAppliedSeedBucket) {
      setBucket(lastAppliedSeedBucket)
    }
  }, [lastAppliedSeedBucket])

  const seedFormBaselineKey = lastAppliedSeedAt?.toISOString() ?? null

  useEffect(() => {
    if (!lastAppliedSeedBucket || !seedFormBaselineKey) return
    if (Object.keys(persistedSeedSubtopicStates).length === 0) return

    setSubtopicFields(
      Object.fromEntries(
        resolvedCatalogTopics.flatMap((topic) =>
          topic.subtopics.map((subtopic) => {
            const persisted = persistedSeedSubtopicStates[subtopic.id]
            const fields = persisted
              ? fieldsFromPersisted(persisted)
              : buildDefaultSubtopicFields(
                  lastAppliedSeedBucket,
                  topic.slug,
                  subtopic.slug,
                )
            return [subtopic.id, fields] as const
          }),
        ),
      ),
    )
    setSubtopicIncluded(
      Object.fromEntries(
        resolvedCatalogTopics.flatMap((topic) =>
          topic.subtopics.flatMap((subtopic) => {
            const persisted = persistedSeedSubtopicStates[subtopic.id]
            if (persisted?.isEnabled === false) {
              return [[subtopic.id, false] as const]
            }
            return []
          }),
        ),
      ),
    )
  }, [
    lastAppliedSeedBucket,
    persistedSeedSubtopicStates,
    resolvedCatalogTopics,
    seedFormBaselineKey,
  ])

  const bucketChangedFromLastApplied =
    lastAppliedSeedBucket !== null && bucket !== lastAppliedSeedBucket

  const summary = useMemo(() => {
    const subtopicCount = resolvedCatalogTopics.reduce(
      (acc, t) => acc + t.subtopics.length,
      0,
    )
    return {
      topicCount: resolvedCatalogTopics.length,
      subtopicCount,
    }
  }, [resolvedCatalogTopics])

  const seed = SEED_BUCKETS[bucket]

  const getFields = useCallback(
    (id: string, topicSlug: string, subtopicSlug: string): SubtopicFieldForm =>
      ({
        ...buildDefaultSubtopicFields(bucket, topicSlug, subtopicSlug),
        ...subtopicFields[id],
      }) satisfies SubtopicFieldForm,
    [bucket, subtopicFields],
  )

  const getStartingMasteryLabel = useCallback(
    (nextBucket: SeedBucket) => `${String(SEED_BUCKETS[nextBucket].mastery)}%`,
    [],
  )

  const isSubtopicIncluded = useCallback(
    (subtopicId: string) => subtopicIncluded[subtopicId] ?? true,
    [subtopicIncluded],
  )

  const buildPayload = useCallback((): SeedKnowledgeMapPayload => {
    const subtopicOverrides: Record<string, SubtopicSeedOverride> = {}
    for (const t of resolvedCatalogTopics) {
      for (const st of t.subtopics) {
        const defaults = buildDefaultSubtopicFields(bucket, t.slug, st.slug)
        const f = getFields(st.id, t.slug, st.slug)
        const o = diffOverrideFromDefaults(defaults, f) ?? {}
        const included = isSubtopicIncluded(st.id)
        const persisted = persistedSeedSubtopicStates[st.id]
        const wasIncluded =
          persisted !== undefined
            ? persisted.isEnabled
            : subtopicById.get(st.id)?.isEnabled !== false
        if (included !== wasIncluded) {
          o.isEnabled = included
        }
        if (Object.keys(o).length > 0) subtopicOverrides[st.id] = o
      }
    }

    return {
      bucket,
      ...(Object.keys(subtopicOverrides).length > 0
        ? { subtopicOverrides }
        : {}),
    }
  }, [
    bucket,
    getFields,
    isSubtopicIncluded,
    persistedSeedSubtopicStates,
    resolvedCatalogTopics,
    subtopicById,
  ])

  const [applying, setApplying] = useState(false)

  const handleApply = async () => {
    setApplying(true)
    try {
      await seedKnowledgeMap(buildPayload())
      setConfirming(false)
    } finally {
      setApplying(false)
    }
  }

  const auditRowCounts = useMemo(() => {
    const nSub = catalogSubtopicCount ?? summary.subtopicCount
    const nTopic = catalogTopicCount ?? summary.topicCount
    return { nSub, nTopic, total: nSub + nTopic }
  }, [
    catalogSubtopicCount,
    catalogTopicCount,
    summary.subtopicCount,
    summary.topicCount,
  ])

  return (
    <div className="border-border rounded-lg border p-4">
      <div className="mb-2 flex items-center gap-2">
        <div className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 rounded-md p-1.5">
          <Sprout size={16} />
        </div>
        <h3 className="text-foreground text-sm font-semibold">
          Seed knowledge map
        </h3>
        <Badge variant="paused">Admin tool</Badge>
      </div>
      <p className="text-muted-foreground mb-3 text-xs">
        Set initial mastery, stability, and difficulty for every subtopic from a
        starting bucket. Use this when onboarding a new client (or as a baseline
        before the calibration assessment lands). Customize per-subtopic fields;
        excluded subtopics stay in the tree but do not roll up into topic
        mastery.
      </p>
      {lastAppliedSeedBucket && (
        <p className="text-muted-foreground mb-2 text-xs">
          Last applied seed:{' '}
          <span className="text-foreground">
            {SEED_BUCKETS[lastAppliedSeedBucket].label}
          </span>
          {lastAppliedSeedCoachName ? ` by ${lastAppliedSeedCoachName}` : ''}
          {lastAppliedSeedAt ? ` at ${lastAppliedSeedAt.toLocaleString()}` : ''}
          .
          {hasPendingSeedProcessing
            ? ' Run Processing is required to replay prior attempts with this seed.'
            : ''}
        </p>
      )}
      {bucketChangedFromLastApplied && (
        <p className="text-muted-foreground mb-2 text-xs">
          Selected bucket differs from last applied (
          {SEED_BUCKETS[lastAppliedSeedBucket!].label}). Overrides below are
          relative to the selected template.
        </p>
      )}
      {!engineEnabled && (
        <p
          className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
          role="note"
        >
          Starting date must be selected first.
        </p>
      )}

      <div
        role="radiogroup"
        aria-label="Starting bucket"
        className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4"
      >
        {SEED_BUCKET_ORDER.map((b) => {
          const values = SEED_BUCKETS[b]
          const active = b === bucket
          return (
            <button
              key={b}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={`Seed bucket: ${values.label}`}
              onClick={() => {
                setBucket(b)
                setSubtopicFields(
                  Object.fromEntries(
                    resolvedCatalogTopics.flatMap((topic) =>
                      topic.subtopics.map(
                        (subtopic) =>
                          [
                            subtopic.id,
                            buildDefaultSubtopicFields(
                              b,
                              topic.slug,
                              subtopic.slug,
                            ),
                          ] as const,
                      ),
                    ),
                  ),
                )
                setSubtopicIncluded({})
              }}
              disabled={!engineEnabled}
              title={
                !engineEnabled
                  ? 'Starting date must be selected first.'
                  : undefined
              }
              className={
                active
                  ? 'border-primary bg-primary/5 rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50'
                  : 'border-border hover:bg-muted/40 rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50'
              }
            >
              <div className="flex items-center justify-between">
                <span className="text-foreground text-sm font-semibold">
                  {values.label}
                </span>
                <span className="text-foreground tabular-nums text-sm">
                  green band {getStartingMasteryLabel(b)}
                </span>
              </div>
              <p className="text-muted-foreground mt-1 text-[11px]">
                {values.description}
              </p>
              <div className="text-muted-foreground mt-2 grid grid-cols-2 gap-1 text-[10px] uppercase tracking-wide">
                <span>
                  stab{' '}
                  <span className="text-foreground tabular-nums">
                    {values.stabilityDays}d
                  </span>
                </span>
                <span>
                  diff{' '}
                  <span className="text-foreground tabular-nums">
                    {values.difficulty}
                  </span>
                </span>
              </div>
            </button>
          )
        })}
      </div>

      <div className="border-border bg-muted/20 mt-3 rounded-lg border">
        <div className="px-3 py-2 text-sm font-medium">
          <span className="text-foreground">
            Customize per topic & subtopic
          </span>
          <span className="text-muted-foreground ml-2 text-xs font-normal">
            (optional overrides)
          </span>
        </div>
        <div
          className="border-border max-h-[min(70vh,520px)] space-y-4 overflow-y-auto border-t p-3"
          aria-disabled={!engineEnabled}
        >
          {resolvedCatalogTopics.map((topic) => (
            <TopicCustomizeBlock
              key={topic.id}
              bucket={bucket}
              topic={topic}
              topicState={topicById.get(topic.id)}
              subtopicById={subtopicById}
              persistedSeedSubtopicStates={persistedSeedSubtopicStates}
              getFields={getFields}
              onFieldChange={(
                subtopicId: string,
                patch: Partial<SubtopicFieldForm>,
              ) =>
                setSubtopicFields((prev) => ({
                  ...prev,
                  [subtopicId]: {
                    ...prev[subtopicId],
                    ...patch,
                  },
                }))
              }
              isSubtopicIncluded={isSubtopicIncluded}
              onIncludedChange={(subtopicId, value) =>
                setSubtopicIncluded((prev) => ({
                  ...prev,
                  [subtopicId]: value,
                }))
              }
            />
          ))}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted-foreground text-xs">
          Applies to{' '}
          <span className="text-foreground font-medium">
            {catalogSubtopicCount ?? summary.subtopicCount} subtopics
          </span>{' '}
          across{' '}
          <span className="text-foreground font-medium">
            {catalogTopicCount ?? summary.topicCount} topics
          </span>
          . One calc-log entry will list{' '}
          <span className="text-foreground font-medium">
            {auditRowCounts.total} provenance rows
          </span>{' '}
          ({auditRowCounts.nSub} subtopic + {auditRowCounts.nTopic} topic
          rollup).
        </p>
        <button
          type="button"
          onClick={() => setConfirming(true)}
          aria-label={`Apply ${seed.label} seed`}
          disabled={!engineEnabled || applying}
          title={
            !engineEnabled ? 'Starting date must be selected first.' : undefined
          }
          className="bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {applying ? 'Applying…' : 'Apply seed'}
        </button>
      </div>

      {confirming && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="seed-confirm-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirming(false)
          }}
        >
          <div className="bg-background border-border w-full max-w-md rounded-lg border p-5 shadow-lg">
            <h4
              id="seed-confirm-title"
              className="text-foreground text-base font-semibold"
            >
              Confirm knowledge map seed
            </h4>
            <p className="text-muted-foreground mt-2 text-sm">
              This will touch <strong>{auditRowCounts.nSub} subtopic</strong>{' '}
              rows and <strong>{auditRowCounts.nTopic} topic</strong> rollup
              rows in the audit entry&apos;s{' '}
              <code className="text-foreground">rowsAffected</code> list, using
              the &quot;{seed.label}&quot; bucket defaults plus any overrides
              you set above (mastery&nbsp;={seed.mastery}, stability&nbsp;=
              {seed.stabilityDays}d, difficulty&nbsp;={seed.difficulty}).
              <strong> One</strong> calc-log entry will be appended.
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                aria-label="Cancel seed"
                className="text-muted-foreground hover:text-foreground rounded-lg px-3 py-2 text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleApply()}
                disabled={applying}
                aria-label={`Confirm seed apply: ${seed.label}`}
                className="bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {applying ? 'Applying…' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function fieldsFromPersisted(
  persisted: PersistedSeedSubtopicState,
): SubtopicFieldForm {
  return {
    mastery: String(persisted.mastery),
    stabilityDays: String(persisted.stabilityDays),
    difficulty: String(persisted.difficulty),
  }
}

function seedHighlightSurfaceClass(highlight: SeedHighlight | null): string {
  switch (highlight) {
    case 'green':
      return 'bg-emerald-500/[0.05] dark:bg-emerald-400/[0.07]'
    case 'yellow':
      return 'bg-amber-500/[0.05] dark:bg-amber-400/[0.07]'
    case 'red':
      return 'bg-red-500/[0.05] dark:bg-red-400/[0.07]'
    default:
      return ''
  }
}

function seedIncludeBaseline(
  subtopicId: string,
  persisted: PersistedSeedSubtopicState | undefined,
  subtopicById: Map<
    string,
    ReturnType<
      typeof useRecommendationEngineMock
    >['topics'][number]['subtopics'][number]
  >,
): boolean {
  if (persisted !== undefined) return persisted.isEnabled
  return subtopicById.get(subtopicId)?.isEnabled !== false
}

function TopicCustomizeBlock({
  bucket,
  topic,
  topicState,
  subtopicById,
  persistedSeedSubtopicStates,
  getFields,
  onFieldChange,
  isSubtopicIncluded,
  onIncludedChange,
}: {
  bucket: SeedBucket
  topic: RecommendationEngineCatalogTopic
  topicState:
    | ReturnType<typeof useRecommendationEngineMock>['topics'][number]
    | undefined
  subtopicById: Map<
    string,
    ReturnType<
      typeof useRecommendationEngineMock
    >['topics'][number]['subtopics'][number]
  >
  persistedSeedSubtopicStates: Record<string, PersistedSeedSubtopicState>
  getFields: (
    id: string,
    topicSlug: string,
    subtopicSlug: string,
  ) => SubtopicFieldForm
  onFieldChange: (subtopicId: string, patch: Partial<SubtopicFieldForm>) => void
  isSubtopicIncluded: (subtopicId: string) => boolean
  onIncludedChange: (subtopicId: string, value: boolean) => void
}) {
  const startingMastery = averageSubtopicMastery(
    topic.subtopics
      .filter((subtopic) => isSubtopicIncluded(subtopic.id))
      .map((subtopic) => {
        const fields = getFields(subtopic.id, topic.slug, subtopic.slug)
        const parsedMastery = parseSeedMastery(fields.mastery)
        return {
          mastery: parsedMastery ?? subtopicById.get(subtopic.id)?.mastery ?? 0,
          isEnabled: isSubtopicIncluded(subtopic.id),
        }
      }),
  )

  const topicHighlight = resolveTopicSeedHighlight(bucket, topic.slug)
  const topicHasTemplateOverrides = topic.subtopics.some((subtopic) => {
    const defaults = buildDefaultSubtopicFields(
      bucket,
      topic.slug,
      subtopic.slug,
    )
    const fields = getFields(subtopic.id, topic.slug, subtopic.slug)
    const included = isSubtopicIncluded(subtopic.id)
    const persisted = persistedSeedSubtopicStates[subtopic.id]
    const includedChanged =
      included !== seedIncludeBaseline(subtopic.id, persisted, subtopicById)
    return (
      diffOverrideFromDefaults(defaults, fields) !== undefined ||
      includedChanged
    )
  })

  return (
    <div className="border-border rounded-md border">
      <div
        className={cn(
          'flex flex-wrap items-center gap-2 px-3 py-2',
          topicHighlight
            ? seedHighlightSurfaceClass(topicHighlight)
            : 'bg-muted/30',
          topicHasTemplateOverrides &&
            'ring-1 ring-inset ring-sky-400/25 bg-sky-500/[0.04]',
        )}
      >
        <span className="text-foreground text-sm font-semibold">
          {topic.name}
        </span>
        {topicHasTemplateOverrides && (
          <Badge variant="active">Overrides template</Badge>
        )}
        <span className="text-muted-foreground text-[11px]">
          Starting mastery: {startingMastery}
          {topicState ? ` (current ${topicState.topicMastery})` : ''}
        </span>
      </div>
      <div className="divide-border divide-y">
        {topic.subtopics.map((subtopic) => {
          const f = getFields(subtopic.id, topic.slug, subtopic.slug)
          const included = isSubtopicIncluded(subtopic.id)
          const subtopicHighlight = resolveSeedHighlight(
            bucket,
            topic.slug,
            subtopic.slug,
          )
          const defaults = buildDefaultSubtopicFields(
            bucket,
            topic.slug,
            subtopic.slug,
          )
          const persisted = persistedSeedSubtopicStates[subtopic.id]
          const includedChanged =
            included !==
            seedIncludeBaseline(subtopic.id, persisted, subtopicById)
          const hasTemplateOverride =
            diffOverrideFromDefaults(defaults, f) !== undefined ||
            includedChanged
          const currentMastery = parseSeedMastery(f.mastery)
          const currentStability = parseSeedStabilityDays(f.stabilityDays)
          const currentDifficulty = parseSeedDifficulty(f.difficulty)
          return (
            <div
              key={subtopic.id}
              className={cn(
                'flex flex-wrap items-center gap-2 px-3 py-2 text-[11px]',
                seedHighlightSurfaceClass(subtopicHighlight),
                hasTemplateOverride &&
                  'ring-1 ring-inset ring-sky-400/25 bg-sky-500/[0.04]',
              )}
            >
              <span className="text-foreground min-w-[140px] font-medium">
                {subtopic.name}
              </span>
              <label className="text-muted-foreground flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={included}
                  onChange={(e) =>
                    onIncludedChange(subtopic.id, e.target.checked)
                  }
                  className="rounded border"
                  aria-label={`Include ${subtopic.name} in rollups`}
                />
                include
              </label>
              <label className="text-muted-foreground flex flex-col gap-0.5">
                <span className="flex items-center gap-1">
                  mastery
                  <input
                    type="number"
                    value={f.mastery}
                    onChange={(e) =>
                      onFieldChange(subtopic.id, { mastery: e.target.value })
                    }
                    className="bg-background border-border text-foreground w-14 rounded border px-1 py-0.5"
                    min={0}
                    max={100}
                    aria-label={`Mastery seed for ${subtopic.name}`}
                  />
                </span>
                {persisted &&
                  currentMastery !== undefined &&
                  currentMastery !== persisted.mastery && (
                    <DiffMarker
                      previousValue={persisted.mastery}
                      currentValue={currentMastery}
                    />
                  )}
              </label>
              <label className="text-muted-foreground flex flex-col gap-0.5">
                <span className="flex items-center gap-1">
                  stab (d)
                  <input
                    type="number"
                    value={f.stabilityDays}
                    onChange={(e) =>
                      onFieldChange(subtopic.id, {
                        stabilityDays: e.target.value,
                      })
                    }
                    className="bg-background border-border text-foreground w-14 rounded border px-1 py-0.5"
                    min={1}
                    aria-label={`Stability seed for ${subtopic.name}`}
                  />
                </span>
                {persisted &&
                  currentStability !== undefined &&
                  currentStability !== persisted.stabilityDays && (
                    <DiffMarker
                      previousValue={persisted.stabilityDays}
                      currentValue={currentStability}
                    />
                  )}
              </label>
              <label className="text-muted-foreground flex flex-col gap-0.5">
                <span className="flex items-center gap-1">
                  diff
                  <input
                    type="number"
                    value={f.difficulty}
                    onChange={(e) =>
                      onFieldChange(subtopic.id, { difficulty: e.target.value })
                    }
                    className="bg-background border-border text-foreground w-14 rounded border px-1 py-0.5"
                    min={1}
                    max={10}
                    step={0.1}
                    aria-label={`Difficulty seed for ${subtopic.name}`}
                  />
                </span>
                {persisted &&
                  currentDifficulty !== undefined &&
                  currentDifficulty !== persisted.difficulty && (
                    <DiffMarker
                      previousValue={persisted.difficulty}
                      currentValue={currentDifficulty}
                    />
                  )}
              </label>
              {persisted && (
                <span className="text-muted-foreground shrink-0">
                  saved M:{persisted.mastery} S:{persisted.stabilityDays} D:
                  {persisted.difficulty}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
